import React, { useState, useEffect } from 'react';
import API from '../../utils/api';

const Fines = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [fines, setFines] = useState([]);
  const [summary, setSummary] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Add fine modal
  const [showAddFine, setShowAddFine] = useState(false);
  const [newFine, setNewFine] = useState({
    member_id: '', reason: '', amount: ''
  });

  useEffect(() => {
    fetchFines();
    fetchSummary();
    fetchMembers();
  }, []);

  const showMsg = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const fetchFines = async () => {
    try {
      setLoading(true);
      const res = await API.get('/fines');
      setFines(res.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const res = await API.get('/fines/summary');
      setSummary(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await API.get('/members');
      setMembers(res.data.filter(m => m.status === 'approved'));
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddFine = async () => {
    if (!newFine.member_id || !newFine.reason || !newFine.amount) {
      showMsg('Please fill in all fields!', 'error');
      return;
    }
    try {
      await API.post('/fines', newFine);
      showMsg('✅ Fine added!', 'success');
      setShowAddFine(false);
      setNewFine({ member_id: '', reason: '', amount: '' });
      fetchFines();
      fetchSummary();
    } catch (error) {
      showMsg(error.response?.data?.message || 'Error!', 'error');
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await API.put(`/fines/${id}/paid`);
      showMsg('✅ Fine marked as paid!', 'success');
      fetchFines();
      fetchSummary();
    } catch (error) {
      showMsg('Error!', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fine?')) return;
    try {
      await API.delete(`/fines/${id}`);
      showMsg('✅ Fine deleted!', 'success');
      fetchFines();
      fetchSummary();
    } catch (error) {
      showMsg('Error!', 'error');
    }
  };

  const handleGenerateCostumeFines = async () => {
    try {
      const res = await API.post('/fines/generate-costume-fines');
      showMsg(res.data.message, 'success');
      fetchFines();
      fetchSummary();
    } catch (error) {
      showMsg('Error generating fines!', 'error');
    }
  };

  const handleGenerateInstrumentFines = async () => {
    try {
      const res = await API.post('/fines/generate-instrument-fines');
      showMsg(res.data.message, 'success');
      fetchFines();
      fetchSummary();
    } catch (error) {
      showMsg('Error generating fines!', 'error');
    }
  };

  const unpaidFines = fines.filter(f => f.status === 'unpaid');
  const paidFines = fines.filter(f => f.status === 'paid');
  const totalUnpaid = unpaidFines.reduce((sum, f) => sum + parseFloat(f.amount), 0);
  const totalPaid = paidFines.reduce((sum, f) => sum + parseFloat(f.amount), 0);

  const displayFines = activeTab === 'all' ? fines :
    activeTab === 'unpaid' ? unpaidFines : paidFines;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>💰 Fines</h2>
          <p style={styles.subtitle}>Manage member fines and payments</p>
        </div>
        <div style={styles.headerBtns}>
          <button style={styles.addBtn}
            onClick={() => setShowAddFine(true)}>
            + Add Manual Fine
          </button>
          <button style={styles.secondaryBtn}
            onClick={handleGenerateCostumeFines}>
            🔄 Generate Costume Fines
          </button>
          <button style={styles.secondaryBtn}
            onClick={handleGenerateInstrumentFines}>
            🔄 Generate Instrument Fines
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={messageType === 'success' ? styles.successMsg : styles.errorMsg}>
          {message}
        </div>
      )}

      {/* Summary Cards */}
      <div style={styles.cardsRow}>
        <div style={{ ...styles.card, borderTop: '4px solid #e74c3c' }}>
          <p style={styles.cardIcon}>💸</p>
          <p style={styles.cardValue}>₱{totalUnpaid.toFixed(2)}</p>
          <p style={styles.cardLabel}>Total Unpaid</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #2ecc71' }}>
          <p style={styles.cardIcon}>✅</p>
          <p style={styles.cardValue}>₱{totalPaid.toFixed(2)}</p>
          <p style={styles.cardLabel}>Total Collected</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #3498db' }}>
          <p style={styles.cardIcon}>📋</p>
          <p style={styles.cardValue}>{unpaidFines.length}</p>
          <p style={styles.cardLabel}>Unpaid Count</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #9b59b6' }}>
          <p style={styles.cardIcon}>👥</p>
          <p style={styles.cardValue}>{summary.length}</p>
          <p style={styles.cardLabel}>Members with Fines</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: 'all', label: `All (${fines.length})` },
          { id: 'unpaid', label: `💸 Unpaid (${unpaidFines.length})` },
          { id: 'paid', label: `✅ Paid (${paidFines.length})` },
          { id: 'summary', label: '📊 By Member' },
        ].map(tab => (
          <button
            key={tab.id}
            style={activeTab === tab.id ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* FINES LIST */}
      {activeTab !== 'summary' && (
        <div style={styles.tableBox}>
          {loading ? (
            <p style={styles.loadingText}>Loading...</p>
          ) : displayFines.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>💰</p>
              <p style={styles.emptyText}>No fines found!</p>
            </div>
          ) : (
            displayFines.map(fine => (
              <div key={fine.id} style={styles.fineCard}>
                <div style={styles.fineLeft}>
                  <div style={{
                    ...styles.fineIcon,
                    background: fine.status === 'unpaid' ? '#ffe0e0' : '#e0ffe0',
                  }}>
                    {fine.status === 'unpaid' ? '💸' : '✅'}
                  </div>
                  <div style={styles.fineInfo}>
                    <p style={styles.fineMember}>
                      {fine.first_name} {fine.last_name}
                      <span style={styles.fineId}> · {fine.school_id}</span>
                    </p>
                    <p style={styles.fineReason}>{fine.reason}</p>
                    <p style={styles.fineDate}>
                      {new Date(fine.created_at).toLocaleDateString('en-PH')}
                      {fine.created_by_fname &&
                        ` · Added by ${fine.created_by_fname} ${fine.created_by_lname}`
                      }
                    </p>
                  </div>
                </div>
                <div style={styles.fineRight}>
                  <p style={{
                    ...styles.fineAmount,
                    color: fine.status === 'unpaid' ? '#e74c3c' : '#2ecc71'
                  }}>
                    ₱{parseFloat(fine.amount).toFixed(2)}
                  </p>
                  <span style={{
                    ...styles.statusPill,
                    background: fine.status === 'unpaid' ? '#ffe0e0' : '#e0ffe0',
                    color: fine.status === 'unpaid' ? '#cc0000' : '#006600',
                  }}>
                    {fine.status}
                  </span>
                  <div style={styles.fineActions}>
                    {fine.status === 'unpaid' && (
                      <button style={styles.paidBtn}
                        onClick={() => handleMarkPaid(fine.id)}>
                        ✓ Paid
                      </button>
                    )}
                    <button style={styles.deleteBtn}
                      onClick={() => handleDelete(fine.id)}>
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* SUMMARY BY MEMBER */}
      {activeTab === 'summary' && (
        <div style={styles.tableBox}>
          {summary.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>👥</p>
              <p style={styles.emptyText}>No fines recorded yet!</p>
            </div>
          ) : (
            <div style={styles.reportTable}>
              <div style={styles.tableHeader}>
                <span style={styles.tableCol}>Member</span>
                <span style={styles.tableCol}>School ID</span>
                <span style={styles.tableCol}>Total Fines</span>
                <span style={{ ...styles.tableCol, color: '#e74c3c' }}>Unpaid</span>
                <span style={{ ...styles.tableCol, color: '#2ecc71' }}>Paid</span>
              </div>
              {summary.map(s => (
                <div key={s.id} style={styles.tableRow}>
                  <span style={styles.tableCol}>
                    {s.first_name} {s.last_name}
                  </span>
                  <span style={styles.tableCol}>{s.school_id}</span>
                  <span style={styles.tableCol}>{s.total_fines}</span>
                  <span style={{
                    ...styles.tableCol,
                    color: '#e74c3c',
                    fontWeight: 'bold'
                  }}>
                    ₱{parseFloat(s.unpaid_amount || 0).toFixed(2)}
                  </span>
                  <span style={{
                    ...styles.tableCol,
                    color: '#2ecc71',
                    fontWeight: 'bold'
                  }}>
                    ₱{parseFloat(s.paid_amount || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ADD FINE MODAL */}
      {showAddFine && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>💰 Add Manual Fine</h3>
            <div style={styles.field}>
              <label style={styles.label}>Member *</label>
              <select style={styles.input}
                value={newFine.member_id}
                onChange={e => setNewFine({
                  ...newFine, member_id: e.target.value
                })}>
                <option value="">Select member</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.first_name} {m.last_name} ({m.school_id})
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Reason *</label>
              <input style={styles.input} type="text"
                placeholder="ex. Absent without notice"
                value={newFine.reason}
                onChange={e => setNewFine({
                  ...newFine, reason: e.target.value
                })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Amount (₱) *</label>
              <input style={styles.input} type="number"
                placeholder="ex. 50"
                min="1"
                value={newFine.amount}
                onChange={e => setNewFine({
                  ...newFine, amount: e.target.value
                })} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => setShowAddFine(false)}>
                Cancel
              </button>
              <button style={styles.doneBtn}
                onClick={handleAddFine}>
                Add Fine
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '0' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  title: { margin: '0 0 5px 0', fontSize: '22px', color: '#333' },
  subtitle: { margin: 0, color: '#888', fontSize: '13px' },
  headerBtns: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  addBtn: {
    padding: '10px 18px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
  },
  secondaryBtn: {
    padding: '10px 18px', background: 'white',
    color: '#0f3460', border: '1.5px solid #0f3460',
    borderRadius: '10px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '13px',
  },
  successMsg: {
    background: '#e0ffe0', color: '#006600',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  errorMsg: {
    background: '#ffe0e0', color: '#cc0000',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  cardsRow: {
    display: 'flex', gap: '15px',
    marginBottom: '20px', flexWrap: 'wrap',
  },
  card: {
    flex: '1', minWidth: '140px', background: 'white',
    borderRadius: '12px', padding: '20px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  cardIcon: { fontSize: '28px', margin: '0 0 8px 0' },
  cardValue: {
    fontSize: '22px', fontWeight: 'bold',
    color: '#333', margin: '0 0 5px 0',
  },
  cardLabel: { fontSize: '12px', color: '#888', margin: 0 },
  tabs: {
    display: 'flex', gap: '8px',
    marginBottom: '20px', flexWrap: 'wrap',
  },
  tab: {
    padding: '8px 18px', background: 'white',
    border: '1.5px solid #ddd', borderRadius: '20px',
    cursor: 'pointer', fontSize: '13px', color: '#666',
  },
  tabActive: {
    padding: '8px 18px', background: '#0f3460',
    border: '1.5px solid #0f3460', borderRadius: '20px',
    cursor: 'pointer', fontSize: '13px',
    color: 'white', fontWeight: 'bold',
  },
  tableBox: {
    background: 'white', borderRadius: '16px',
    padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  loadingText: { textAlign: 'center', color: '#888', padding: '30px' },
  emptyState: { textAlign: 'center', padding: '40px 0' },
  emptyIcon: { fontSize: '40px', margin: '0 0 10px 0' },
  emptyText: { color: '#888', fontSize: '14px', margin: 0 },
  fineCard: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '15px',
    background: '#f8f9fa', borderRadius: '10px',
    marginBottom: '10px', flexWrap: 'wrap', gap: '10px',
  },
  fineLeft: {
    display: 'flex', alignItems: 'center',
    gap: '12px', flex: 1,
  },
  fineIcon: {
    width: '44px', height: '44px',
    borderRadius: '10px', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: '20px', flexShrink: 0,
  },
  fineInfo: { flex: 1 },
  fineMember: {
    margin: '0 0 4px 0', fontWeight: 'bold',
    color: '#333', fontSize: '14px',
  },
  fineId: { color: '#888', fontWeight: 'normal', fontSize: '12px' },
  fineReason: { margin: '0 0 3px 0', color: '#555', fontSize: '13px' },
  fineDate: { margin: 0, color: '#aaa', fontSize: '12px' },
  fineRight: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'flex-end', gap: '6px',
  },
  fineAmount: {
    margin: 0, fontSize: '18px', fontWeight: 'bold',
  },
  statusPill: {
    padding: '3px 10px', borderRadius: '20px',
    fontSize: '11px', fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  fineActions: { display: 'flex', gap: '6px' },
  paidBtn: {
    padding: '6px 14px', background: '#2ecc71',
    color: 'white', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '12px',
  },
  deleteBtn: {
    padding: '6px 10px', background: 'none',
    border: '1px solid #ddd', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px',
  },
  reportTable: { overflowX: 'auto' },
  tableHeader: {
    display: 'flex', padding: '12px 15px',
    background: '#f8f9fa', borderRadius: '8px',
    marginBottom: '5px', fontWeight: 'bold',
    fontSize: '12px', color: '#666', textTransform: 'uppercase',
  },
  tableRow: {
    display: 'flex', padding: '12px 15px',
    borderBottom: '1px solid #f5f5f5',
    fontSize: '13px', color: '#333', alignItems: 'center',
  },
  tableCol: { flex: 1, minWidth: '100px' },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 1001,
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '16px',
    padding: '30px', width: '100%', maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalTitle: {
    margin: '0 0 20px 0', fontSize: '18px',
    fontWeight: 'bold', color: '#333',
  },
  field: { marginBottom: '15px' },
  label: {
    display: 'block', fontSize: '13px',
    fontWeight: '600', color: '#444', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px',
    borderRadius: '10px', border: '1.5px solid #ddd',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '20px' },
  cancelBtn: {
    flex: 1, padding: '12px', background: 'white',
    color: '#666', border: '1.5px solid #ddd',
    borderRadius: '10px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '14px',
  },
  doneBtn: {
    flex: 1, padding: '12px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
  },
};

export default Fines;